import { z } from "zod";

/** ຄວາມຍາວຊົ່ວໂມງຮຽນ: ທີລະ 15 ນາທີ ຫາ 180 ນາທີ — ໃຊ້ຮ່ວມຟອມຈອງດຽວ ແລະ bulk */
export const PRIVATE_LESSON_SLOT_DURATION_CHOICES = [
  15, 30, 45, 60, 75, 90, 105, 120, 135, 150, 165, 180,
] as const;

export type PrivateLessonSlotDurationMinutes =
  (typeof PRIVATE_LESSON_SLOT_DURATION_CHOICES)[number];

export const PRIVATE_LESSON_SLOT_DURATION_SET = new Set<number>(
  PRIVATE_LESSON_SLOT_DURATION_CHOICES,
);

export const PrivateLessonSlotDurationMinutesSchema = z.union([
  z.literal(15),
  z.literal(30),
  z.literal(45),
  z.literal(60),
  z.literal(75),
  z.literal(90),
  z.literal(105),
  z.literal(120),
  z.literal(135),
  z.literal(150),
  z.literal(165),
  z.literal(180),
]);

/** 15→0.25, 30→0.5, … 180→3 — ສຳລັບ slot ທີ່ກຳນົດໄວ້ */
const SLOT_MINUTES_TO_HOURS_LABEL: Record<
  PrivateLessonSlotDurationMinutes,
  string
> = {
  15: "0.25",
  30: "0.5",
  45: "0.75",
  60: "1",
  75: "1.25",
  90: "1.50",
  105: "1.75",
  120: "2",
  135: "2.25",
  150: "2.50",
  165: "2.75",
  180: "3",
};

/** ແປງນາທີ → ຊົ່ວໂມງ (ທຸກ 15 ນາທີ = 0.25 ຊົ່ວໂມງ) */
export function minutesToSlotHours(minutes: number): number {
  return minutes / 60;
}

/** ຜົນລວມນາທີຫລາຍຄາບ → ຊົ່ວໂມງ (ບໍ່ປັດຂຶ້ນ) */
export function sumMinutesToSlotHours(totalMinutes: number): number {
  return minutesToSlotHours(totalMinutes);
}

type BulkSessionDurationSource = {
  slotDurationMinutes?: number;
  startTime?: string;
  endTime?: string;
};

/** ໃຊ້ slotDurationMinutes ເປັນຫລັກ (ບໍ່ infer ຈາກ end−start ທີ່ອາດຄ້າງຄ່າເກົ່າ) */
export function resolveBulkSessionSlotDurationMinutes(
  session: BulkSessionDurationSource,
): number {
  if (
    session.slotDurationMinutes != null &&
    PRIVATE_LESSON_SLOT_DURATION_SET.has(session.slotDurationMinutes)
  ) {
    return session.slotDurationMinutes;
  }
  if (session.startTime && session.endTime) {
    const start = new Date(`2000-01-01T${session.startTime}`);
    const end = new Date(`2000-01-01T${session.endTime}`);
    const mins = Math.round((end.getTime() - start.getTime()) / 60_000);
    if (mins > 0 && PRIVATE_LESSON_SLOT_DURATION_SET.has(mins)) {
      return mins;
    }
    if (mins > 0) return mins;
  }
  return 0;
}

export function sumBulkSessionSlotDurationMinutes(
  sessions: BulkSessionDurationSource[],
): number {
  return sessions.reduce(
    (total, session) =>
      total + resolveBulkSessionSlotDurationMinutes(session),
    0,
  );
}

/** ສະແດງຊົ່ວໂມງ: 0.25, 0.5, 1, 1.50, 2 … */
export function formatSlotHours(hours: number): string {
  const rounded = Math.round(hours * 100) / 100;
  if (Number.isInteger(rounded)) {
    return String(rounded);
  }
  const asQuarterSlot = rounded * 60;
  if (
    PRIVATE_LESSON_SLOT_DURATION_SET.has(asQuarterSlot) &&
    asQuarterSlot in SLOT_MINUTES_TO_HOURS_LABEL
  ) {
    return SLOT_MINUTES_TO_HOURS_LABEL[
      asQuarterSlot as PrivateLessonSlotDurationMinutes
    ];
  }
  const fixed = rounded.toFixed(2);
  return fixed.endsWith("0") ? rounded.toFixed(1) : fixed;
}

/** ສະແດງຊົ່ວໂມງຈາກນາທີ */
export function formatSlotHoursFromMinutes(minutes: number): string {
  if (PRIVATE_LESSON_SLOT_DURATION_SET.has(minutes)) {
    return SLOT_MINUTES_TO_HOURS_LABEL[
      minutes as PrivateLessonSlotDurationMinutes
    ];
  }
  return formatSlotHours(minutesToSlotHours(minutes));
}

/** ປ້າຍ Lao: "90 ນາທີ (1.50 ຊົ່ວໂມງ)" */
export function formatSlotDurationLaoLabel(minutes: number): string | null {
  if (minutes <= 0) return null;
  return `${minutes} \u0e99\u0eb2\u0e97\u0eb5 (${formatSlotHoursFromMinutes(minutes)} \u0e8a\u0ebb\u0ec8\u0ea7\u0ec2\u0ea1\u0e87)`;
}

export const PRIVATE_LESSON_DURATION_SELECT_OPTIONS =
  PRIVATE_LESSON_SLOT_DURATION_CHOICES.map((n) => ({
    value: String(n),
    label: `${n} \u0e99\u0eb2\u0e97\u0eb5 (${formatSlotHoursFromMinutes(n)} \u0e8a\u0ebb\u0ec8\u0ea7\u0ec2\u0ea1\u0e87)`,
  }));

/** ຈາກນາທີໃດໆົ → ເລີ່ມທີ່ໃກ້ທີ່ສຸດໃນລາຍການ 15–180 ທີລະ 15 */
export function nearestSlotDurationMinutes(
  minutes: number,
): PrivateLessonSlotDurationMinutes {
  let best: PrivateLessonSlotDurationMinutes = 60;
  let dist = Number.POSITIVE_INFINITY;
  for (const q of PRIVATE_LESSON_SLOT_DURATION_CHOICES) {
    const d = Math.abs(q - minutes);
    if (d < dist) {
      dist = d;
      best = q;
    }
  }
  return best;
}
